import React, { useMemo, useRef, useState } from "react";
import { Dices, RotateCcw, Check } from "lucide-react";
import { clubSeasons } from "../../data/gameData";
import { ShirtKit } from "../fpl/ShirtKit";

const TAKER_POS = ["ST", "LW", "RW", "CM", "LM", "RM"];

const pools = () => {
  const all = clubSeasons.flatMap((c) => c.squad.map((p) => ({ ...p, short: c.short, color: c.color, club: c.club })));
  return { taker: all.filter((p) => TAKER_POS.includes(p.pos)), keeper: all.filter((p) => p.pos === "GK") };
};

const toApi = (p) => ({ name: p.name, rating: p.rating, short: p.short, color: p.color, pos: p.pos });

const SpinCard = ({ kind, label, pool, value, onPick, respins, setRespins }) => {
  const [reel, setReel] = useState(null);
  const spinning = reel !== null && !value;
  const timer = useRef(null);

  const spin = () => {
    if (spinning) return;
    if (value) {
      if (respins <= 0) return;
      setRespins(respins - 1);
      onPick(null);
    }
    const final = pool[Math.floor(Math.random() * pool.length)];
    let ticks = 0;
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      ticks += 1;
      setReel(pool[Math.floor(Math.random() * pool.length)]);
      if (ticks > 22) {
        clearInterval(timer.current);
        setReel(final);
        onPick(final);
      }
    }, 65);
  };

  const shown = value || reel;
  return (
    <div data-testid={`spin-card-${kind}`} className="card-38 p-4 hover:translate-y-0 flex flex-col items-center text-center">
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#38BDF8]">{label}</div>
      <div className={`my-3 ${spinning ? "animate-pulse" : value ? "pop-in" : ""}`}>
        <ShirtKit short={shown?.short} color={shown?.color} isGK={kind === "keeper"} empty={!shown} className="w-16 h-16 sm:w-20 sm:h-20 text-white" />
      </div>
      <div className="min-h-[44px]">
        {shown ? (
          <>
            <div data-testid={`spin-${kind}-name`} className="font-display font-bold text-white leading-tight">{shown.name}</div>
            <div className="text-xs text-slate-400">
              {shown.club} · <span className="text-[#10B981] font-bold" data-testid={`spin-${kind}-rating`}>{shown.rating}</span>
            </div>
          </>
        ) : (
          <div className="text-xs text-slate-500 pt-3">Spin to sign one</div>
        )}
      </div>
      <button
        data-testid={`spin-${kind}-btn`}
        onClick={spin}
        disabled={spinning || (value && respins <= 0)}
        className={`mt-3 w-full rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 ${
          value ? "btn-ghost text-white" : "btn-green"
        }`}
      >
        {value ? <><RotateCcw className="w-4 h-4" /> Re-spin ({respins})</> : <><Dices className="w-4 h-4" /> {spinning ? "Spinning…" : "Spin"}</>}
      </button>
    </div>
  );
};

// Spin for a penalty taker + goalkeeper, then lock them in.
export const SpinPick = ({ onConfirm, busy }) => {
  const { taker: takerPool, keeper: keeperPool } = useMemo(pools, []);
  const [taker, setTaker] = useState(null);
  const [keeper, setKeeper] = useState(null);
  const [respins, setRespins] = useState(1);

  return (
    <div data-testid="spin-pick" className="max-w-md mx-auto">
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-white">Sign your shootout duo</h2>
      <p className="text-slate-400 text-sm mt-1 mb-5">
        Spin for a penalty taker and a keeper. Higher ratings mean sharper shots and longer dives. One re-spin.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <SpinCard kind="taker" label="Penalty Taker" pool={takerPool} value={taker} onPick={setTaker} respins={respins} setRespins={setRespins} />
        <SpinCard kind="keeper" label="Goalkeeper" pool={keeperPool} value={keeper} onPick={setKeeper} respins={respins} setRespins={setRespins} />
      </div>
      <button
        data-testid="confirm-squad-btn"
        disabled={!taker || !keeper || busy}
        onClick={() => onConfirm(toApi(taker), toApi(keeper))}
        className="btn-green wager-glow w-full rounded-xl py-4 mt-5 font-extrabold text-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:animate-none"
      >
        <Check className="w-5 h-5" /> {busy ? "Locking in…" : "Lock in & walk out"}
      </button>
    </div>
  );
};

export default SpinPick;
